import { HEATMAP_LUT, COLOR_SCHEMES } from '../constants/design-tokens';
import { ColorSchemeId } from '../types/enums';

/**
 * Color LUT (Look-Up Table) utilities for pressure heatmap rendering.
 *
 * The 86-point pressure sensor array is visualized by mapping each normalized
 * pressure value (0-1) through a precomputed 256-entry RGBA lookup table.
 * This avoids per-pixel color string parsing during canvas rendering.
 */

/**
 * Build a 256-entry color lookup table from color stops.
 *
 * @param stops - Array of [position, hexColor] tuples, position in [0, 1].
 *                Must be sorted ascending by position. Example:
 *                `[[0.0, '#1e3a8a'], [0.33, '#22D3EE'], [1.0, '#EF4444']]`
 * @param size - Number of entries in the LUT (default 256).
 * @returns RGBA bytes packed into a Uint8ClampedArray of length `size * 4`.
 */
export function buildLUT(stops: [number, string][], size: number = 256): Uint8ClampedArray {
  const lut = new Uint8ClampedArray(size * 4);
  for (let i = 0; i < size; i++) {
    const t = i / (size - 1);
    // Find the two stops that bracket t
    let lo = stops[0];
    let hi = stops[stops.length - 1];
    for (let s = 0; s < stops.length - 1; s++) {
      if (t >= stops[s][0] && t <= stops[s + 1][0]) {
        lo = stops[s];
        hi = stops[s + 1];
        break;
      }
    }
    const range = hi[0] - lo[0];
    const localT = range === 0 ? 0 : (t - lo[0]) / range;
    const c1 = hexToRgb(lo[1]);
    const c2 = hexToRgb(hi[1]);
    lut[i * 4] = Math.round(c1.r + (c2.r - c1.r) * localT);
    lut[i * 4 + 1] = Math.round(c1.g + (c2.g - c1.g) * localT);
    lut[i * 4 + 2] = Math.round(c1.b + (c2.b - c1.b) * localT);
    lut[i * 4 + 3] = 255;
  }
  return lut;
}

/**
 * Parse a hex color string (`#RRGGBB`) into RGB components.
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const cleaned = hex.replace('#', '');
  const r = parseInt(cleaned.substring(0, 2), 16);
  const g = parseInt(cleaned.substring(2, 4), 16);
  const b = parseInt(cleaned.substring(4, 6), 16);
  return { r, g, b };
}

/**
 * Map a normalized 0-1 value to an LUT index.
 * Values outside [0, 1] are clamped to the endpoints.
 */
export function valueToLutIndex(value: number, lutSize: number = 256): number {
  return Math.max(0, Math.min(lutSize - 1, Math.round(value * (lutSize - 1))));
}

/**
 * Get the RGBA color tuple from a LUT for a normalized value.
 *
 * @returns `[r, g, b, a]` tuple (each 0-255).
 */
export function getColorFromLUT(
  lut: Uint8ClampedArray,
  value: number
): [number, number, number, number] {
  const idx = valueToLutIndex(value, lut.length / 4) * 4;
  return [lut[idx], lut[idx + 1], lut[idx + 2], lut[idx + 3]];
}

/**
 * Get the LUT for a specific color scheme.
 * Falls back to HEATMAP_LUT if the scheme is not found.
 */
export function getLUTForScheme(schemeId: ColorSchemeId): Uint8ClampedArray {
  const scheme = COLOR_SCHEMES[schemeId];
  return buildLUT(scheme.stops as [number, string][]);
}

/**
 * Default LUT (built once from HEATMAP_LUT).
 * Useful for callers that don't need scheme switching.
 */
export const DEFAULT_LUT: Uint8ClampedArray = buildLUT(HEATMAP_LUT);

/**
 * Render an insole-shaped pressure heatmap to a canvas context.
 *
 * Each pressure point is drawn as a radial gradient blob whose color is taken
 * from the LUT. Overlapping blobs produce a smooth interpolated heatmap, and
 * the whole thing is clipped to a rounded-rectangle insole outline.
 */
export function renderPressureHeatmap(
  ctx: CanvasRenderingContext2D,
  points: { index: number; x: number; y: number; value: number }[],
  lut: Uint8ClampedArray,
  options: {
    width: number;
    height: number;
    insoleBounds: { minX: number; maxX: number; minY: number; maxY: number };
    pointRadius?: number;
    smoothing?: number;
  }
): void {
  const { width, height, insoleBounds, pointRadius = 12, smoothing = 2 } = options;

  // Clear
  ctx.clearRect(0, 0, width, height);

  // Scale factors: map insole coordinates to canvas coordinates
  const scaleX = width / (insoleBounds.maxX - insoleBounds.minX);
  const scaleY = height / (insoleBounds.maxY - insoleBounds.minY);
  const scale = Math.min(scaleX, scaleY);
  const offsetX = (width - (insoleBounds.maxX - insoleBounds.minX) * scale) / 2;
  const offsetY = (height - (insoleBounds.maxY - insoleBounds.minY) * scale) / 2;

  // Draw insole shape outline (rounded rectangle approximating insole)
  ctx.save();
  ctx.beginPath();
  const padding = 4;
  ctx.roundRect(
    offsetX + padding,
    offsetY + padding,
    width - 2 * offsetX - 2 * padding,
    height - 2 * offsetY - 2 * padding,
    20
  );
  ctx.clip();

  // Fill background
  ctx.fillStyle = 'rgba(10, 10, 15, 0.8)';
  ctx.fillRect(0, 0, width, height);

  // Draw pressure points as radial gradients
  for (const point of points) {
    const cx = offsetX + (point.x - insoleBounds.minX) * scale;
    const cy = offsetY + (point.y - insoleBounds.minY) * scale;
    const [r, g, b, a] = getColorFromLUT(lut, point.value);

    const gradient = ctx.createRadialGradient(cx, cy, 0, cx, cy, pointRadius * smoothing);
    gradient.addColorStop(0, `rgba(${r},${g},${b},${a / 255})`);
    gradient.addColorStop(1, `rgba(${r},${g},${b},0)`);

    ctx.fillStyle = gradient;
    ctx.beginPath();
    ctx.arc(cx, cy, pointRadius * smoothing, 0, Math.PI * 2);
    ctx.fill();
  }

  ctx.restore();

  // Draw insole outline
  ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.roundRect(
    offsetX + padding,
    offsetY + padding,
    width - 2 * offsetX - 2 * padding,
    height - 2 * offsetY - 2 * padding,
    20
  );
  ctx.stroke();
}
